import Config from '../../../../lib/Config'
import { getClientIp } from '../../../../lib/ClientIp'
import { Language } from '../../../../lib/Enums/Language'
import { Budget } from '../../../../lib/Types'
import { SheetType } from '../Enums/SheetType'

export interface ChatMessage {
  role: 'assistant' | 'user'
  content: string
}

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
})

export interface ChatUsage {
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
}

export interface ChatResult {
  text: string
  // True when the agent emitted `[FINISHED]`, signalling the conversation is
  // complete. (`[DONE]` merely terminates each individual message stream.)
  done: boolean
  // The budget JSON the agent appended after `[FINISHED]` (present when done).
  budget?: unknown
  // Token usage reported by the final SSE event, when present.
  usage?: ChatUsage
}

/**
 * POSTs the conversation to /ai/ and streams the assistant reply back.
 * The endpoint returns Server-Sent-Events whose `data:` payload is the raw
 * text chunk (not JSON); each chunk is forwarded verbatim via onToken.
 * Resolves with the fully assembled reply and whether the agent is done.
 */
export const streamChat = async (
  messages: ChatMessage[],
  onToken: (token: string) => void,
  language: Language,
): Promise<ChatResult> => {
  const ip = await getClientIp()
  const response = await fetch(`${Config.API}/ai/`, {
    method: 'POST',
    headers: {
      ...getHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ip, language, messages }),
  })

  if (!response.ok || !response.body) {
    throw response.status
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let full = ''
  let usage: ChatUsage | undefined

  // An SSE event is one or more lines terminated by a blank line. Concatenate
  // the `data:` lines (stripping the field prefix and a single optional
  // leading space per the SSE spec) so the JSON payload survives intact.
  const handleEvent = (event: string) => {
    const chunk = event
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => {
        const value = line.slice('data:'.length)
        return value.startsWith(' ') ? value.slice(1) : value
      })
      .join('\n')

    // `[DONE]` terminates every message stream — ignore it. `[FINISHED]` (with
    // the budget JSON after it) arrives as part of the content, so let it
    // accumulate and split it out once the stream ends.
    if (chunk === '[DONE]') return
    if (!chunk) return

    // Each event's data is a JSON object `{ "response": "<text chunk>" }`, and
    // the final event may also carry a `usage` object (with an empty response).
    // Pull the text out; fall back to the raw chunk if it isn't JSON.
    let token = chunk
    try {
      const parsed = JSON.parse(chunk)
      if (typeof parsed?.response === 'string') token = parsed.response
      if (parsed?.usage) usage = parsed.usage
    } catch {
      // Not JSON — treat the raw data as the text chunk.
    }
    if (!token) return

    full += token
    onToken(token)
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // Events are separated by a blank line. Keep the trailing partial event
    // buffered for the next read.
    const events = buffer.replace(/\r\n/g, '\n').split('\n\n')
    buffer = events.pop() ?? ''
    events.forEach(handleEvent)
  }

  // Flush any final event left in the buffer once the stream ends.
  if (buffer) handleEvent(buffer)

  // DEBUG: log the complete agent response once the stream has finished.
  // eslint-disable-next-line no-console
  console.log('[AI response complete]', JSON.stringify(full))

  // `[FINISHED]` marks completion; the budget JSON follows it. Split the
  // human-readable message from the JSON payload.
  const finishedAt = full.indexOf('[FINISHED]')
  if (finishedAt === -1) {
    return { text: full, done: false, usage }
  }

  const text = full.slice(0, finishedAt).trim()
  const jsonPart = full.slice(finishedAt + '[FINISHED]'.length).trim()
  let budget: unknown = null
  try {
    budget = JSON.parse(jsonPart)
  } catch {
    budget = null
  }

  return { text, done: true, budget, usage }
}

// Posts the agent-generated budget JSON to /ai/complete so the backend can
// create the budget, with the conversation's token usage under `usage` and the
// messages sent to the model under `messages`. Resolves with the created budget.
export const completeChat = async (
  budget: unknown,
  usage?: ChatUsage,
  messages?: ChatMessage[],
): Promise<Budget> => {
  const ip = await getClientIp()
  const response = await fetch(`${Config.API}/ai/complete/`, {
    method: 'POST',
    headers: {
      ...getHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...(budget as object), ip, usage, messages }),
  })

  if (!response.ok) {
    throw response.status
  }

  const created = await response.json()
  return { ...created, sheet_type: SheetType.BUDGET }
}
